import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { AgentConversationRead } from './schemas'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import { createOrUpdateConversationFromServerRead } from '../util/create-or-update-conversation-from-read'

/**
 * Wrapper around AgentConversationRead (from the backend)
 * with additional UI-related fields.
 * Potentially move into a types.ts file?
 */
export type AgentConversation = AgentConversationRead & {
  lastRefresh: number | null
  isLoading: boolean
  loadError: ConversationLoadError | null
}

export type ConversationLoadError = {
  code: string
  title: string
  message: string
}

/**
 * Per-matter fetch state for the conversation list. The conversations themselves live in the flat
 * `conversations` record; this only tracks the refresh lifecycle for one matter's list.
 */
export type MatterConversationListEntry = {
  loading: boolean
  error: boolean
  /* Set once the first fetch for this matter succeeds; a later failed or in-flight refresh never resets it, so the loaded list stays visible. */
  initialLoadComplete: boolean
  /* Monotonic id of the latest fetch for this matter; a response tagged with an older id is dropped so a slow refresh can't overwrite fresher data. */
  requestId: number
  /* Timestamp of the last successful fetch, used by the listener's per-matter cache guard. */
  lastRefresh: number | null
}

export type AgentConversationsState = {
  conversations: Partial<{ [conversationId: string]: AgentConversation }>
  activeContentViewer: Partial<{ [conversationId: string]: ContentViewerSession }>
  conversationListByMatterId: Partial<{ [matterId: string]: MatterConversationListEntry }>
}

const initialState: AgentConversationsState = {
  conversations: {},
  activeContentViewer: {},
  conversationListByMatterId: {},
}

/**
 * Agent Conversations Slice
 * Holds new agent conversations.
 */
export const agentConversationsSlice = createSlice({
  name: 'agentConversationsState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterConversationList: (_state, _action: PayloadAction<{ matterId: string | null; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },
    generateConversationTitle: (_state, _action: PayloadAction<{ conversationId: string }>) => {
      // Handled by listener middleware
    },
    fetchConversationData: (_state, _action: PayloadAction<{ conversationId: string; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },
    generateConversationFollowUpQuestions: (_state, _action: PayloadAction<{ conversationId: string }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    /**
     * Upsert a single AgentConversation from an AgentConversationRead
     * Skips the update if the conversation already exists and has the same `updated_at` timestamp.
     */
    upsertConversationFromRead: (state, action: PayloadAction<AgentConversationRead>) => {
      const conversationRead = action.payload
      const conversation = createOrUpdateConversationFromServerRead(state, conversationRead)

      state.conversations[conversation.id] = conversation
    },

    /**
     * Claim the latest conversation-list fetch for a matter: bump its request id and enter the loading state.
     * A sync or failure whose id no longer matches the claim is dropped.
     */
    fetchMatterConversationsStarted: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const existing = state.conversationListByMatterId[matterId]
      state.conversationListByMatterId[matterId] = {
        loading: true,
        error: false,
        initialLoadComplete: existing?.initialLoadComplete ?? false,
        requestId,
        lastRefresh: existing?.lastRefresh ?? null,
      }
    },

    /** Reconcile one matter's conversations from its list response. */
    syncMatterConversationsFromReadArray: (
      state,
      action: PayloadAction<{ matterId: string; conversations: AgentConversationRead[]; requestId: number; lastRefresh: number }>
    ) => {
      const { matterId, conversations, requestId, lastRefresh } = action.payload
      const existing = state.conversationListByMatterId[matterId]
      // Only the fetch the matter is currently waiting on may write. Drop the response if the entry
      // was removed (e.g. the matter was deleted mid-flight) or another fetch has since claimed it,
      // so a slow response can't resurrect a removed matter, overwrite fresher data, or clobber a
      // just-created conversation.
      if (!existing || requestId !== existing.requestId) return

      const incomingIds = new Set(conversations.map((conversationRead) => conversationRead.id))

      // Drop this matter's conversations the server no longer returns (so deletes elsewhere still
      // propagate), scoped by each conversation's current matter_id so other matters are untouched.
      for (const conversationId of Object.keys(state.conversations)) {
        const conversation = state.conversations[conversationId]
        if (conversation && conversation.matter_id === matterId && !incomingIds.has(conversationId)) {
          delete state.conversations[conversationId]
        }
      }

      // Upsert each conversation, preserving its local UI state
      conversations.forEach((conversationRead) => {
        const conversation = createOrUpdateConversationFromServerRead(state, conversationRead)
        state.conversations[conversation.id] = conversation
      })

      state.conversationListByMatterId[matterId] = {
        loading: false,
        error: false,
        initialLoadComplete: true,
        requestId,
        lastRefresh,
      }
    },

    /** Mark a matter's conversation-list fetch as failed, unless another fetch has since claimed the matter. */
    fetchMatterConversationsFailed: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const entry = state.conversationListByMatterId[matterId]
      if (!entry || requestId !== entry.requestId) return
      entry.loading = false
      entry.error = true
    },

    /** Drop all of a matter's conversations and its per-matter fetch state, e.g. when the matter is deleted. */
    removeMatterConversations: (state, action: PayloadAction<{ matterId: string }>) => {
      const { matterId } = action.payload
      for (const conversationId of Object.keys(state.conversations)) {
        if (state.conversations[conversationId]?.matter_id === matterId) {
          delete state.conversations[conversationId]
        }
      }
      delete state.conversationListByMatterId[matterId]
    },

    // Delete a conversation
    deleteConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      delete state.conversations[conversationId]
    },

    // Set loading state for a conversation
    setConversationLoading: (state, action: PayloadAction<{ conversationId: string; isLoading: boolean }>) => {
      const { conversationId, isLoading } = action.payload
      const conversation = state.conversations[conversationId]
      if (!conversation) return

      conversation.isLoading = isLoading
    },

    // Set error state for a conversation
    setConversationError: (state, action: PayloadAction<{ conversationId: string; loadingError: ConversationLoadError | null }>) => {
      const { conversationId, loadingError } = action.payload
      const conversation = state.conversations[conversationId]
      if (!conversation) return

      conversation.loadError = loadingError
    },

    // Set the last refresh time for a conversation
    setConversationLastRefresh: (state, action: PayloadAction<{ conversationId: string; lastRefresh: number | null }>) => {
      const { conversationId, lastRefresh } = action.payload
      const conversation = state.conversations[conversationId]
      if (!conversation) return

      conversation.lastRefresh = lastRefresh
    },

    // Set the active content viewer session for a conversation
    setConversationActiveContentViewerSession: (state, action: PayloadAction<{ conversationId: string; session: ContentViewerSession }>) => {
      const { conversationId, session } = action.payload
      if (!state.conversations[conversationId]) return

      state.activeContentViewer[conversationId] = session
    },

    // Remove the active content viewer for a conversation
    removeConversationActiveContentViewer: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      delete state.activeContentViewer[conversationId]
    },

    // Update the matter associated with a conversation
    updateConversationMatter: (state, action: PayloadAction<{ conversationId: string; matterId: string | null }>) => {
      const { conversationId, matterId } = action.payload
      const conversation = state.conversations[conversationId]
      if (!conversation) return

      conversation.matter_id = matterId
    },
  },
})

// Actions
export const AgentConversationsActions = agentConversationsSlice.actions

export default agentConversationsSlice.reducer
